// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  FileInfo,
  Flex,
  GridAlpha,
  Heading,
  Spinner,
  Tag,
  Text,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import { refetchType } from '../../../data';
import {
  LoanDocumentUploadPresignedUrlResponse,
  uploadToPresignedUrlPostRequest,
  useGetLoanDocumentUploadPresignedUrl,
} from '../../../data/loanDocuments';
import { useGetLoanTemplates } from '../../../data/loanTemplates';
import {
  Document,
  LoanApplicationV2Response,
  useGetLoanDocuments,
  useGetMinimalLoan,
  useSetReadyForReview,
  useUserUpdateDocumentStatus,
} from '../../../data/loans';
import { Field, Template, mapDocumentsWithSingleTemplate } from '../admin/AdminLoansFollowupForm';
import { LoansDocumentFileList } from '../components/FileList';
import { UploadDialog } from '../components/UploadDialog';
import { LoansFollowupFormEvents } from '../constants';
import { DocumentStatusMetadata, FileStatus, LoansFollowupFormProps } from './types';

function suffixFilename(fileName: string, timestamp: string) {
  const lastIndexOfDot = fileName.lastIndexOf('.');
  return `${fileName.slice(0, lastIndexOfDot)}.${timestamp}${fileName.slice(lastIndexOfDot)}`;
}

function LoansFollowupForm(props: LoansFollowupFormProps) {
  const { setFileStatus, fileStatus } = props;
  let { loanId }: { loanId: string } = useParams();
  loanId = props.loanId || loanId; // Use loanId from props if available, otherwise use from params

  const { loanTemplates, isFetchingLoanTemplates } = useGetLoanTemplates();
  const { presignedUrlDetails, isFetchingPresignedUrlDetails, refetchPresignedUrlDetails } =
    useGetLoanDocumentUploadPresignedUrl(loanId);
  const { loan, isFetchingLoan } = useGetMinimalLoan(loanId);
  const { documents, isFetchingLoanDocuments, refetchLoanDocuments, isFetchedLoanDocuments } =
    useGetLoanDocuments(loanId);
  const templateMap =
    loanTemplates?.reduce(
      (acc, template) => {
        acc[template.id] = template;
        return acc;
      },
      {} as Record<string, Template>,
    ) ?? {};
  const template = templateMap[loan?.data.attributes?.follow_up_template_id ?? ''] ?? null;

  const isFormReady =
    !isFetchingLoanTemplates &&
    !isFetchingLoan &&
    (!isFetchingLoanDocuments || isFetchedLoanDocuments) &&
    !isFetchingPresignedUrlDetails;

  const isInvalidState =
    !templateMap ||
    Object.entries(templateMap).length < 1 ||
    !loan ||
    !template ||
    !documents ||
    !presignedUrlDetails;

  if (!isFormReady) {
    return (
      <Flex align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isInvalidState) {
    return <Redirect to="/500" />;
  }

  return (
    <>
      <Box pt={4}>
        <DataSectionComponent
          documents={documents}
          template={template}
          presignedUrlDetails={presignedUrlDetails}
          loan={loan}
          fileStatus={fileStatus}
          setFileStatus={setFileStatus}
          refetchPresignedUrlDetails={refetchPresignedUrlDetails}
          refetchDocuments={refetchLoanDocuments}
          onSubmit={props.onSubmit}
        />
      </Box>
    </>
  );
}

type DataSectionComponentProps = {
  loan: LoanApplicationV2Response;
  template: Template;
  documents: Document[];
  refetchDocuments: refetchType;
  refetchPresignedUrlDetails: refetchType;
  fileStatus: FileStatus[];
  setFileStatus: React.Dispatch<React.SetStateAction<FileStatus[]>>;
  presignedUrlDetails: LoanDocumentUploadPresignedUrlResponse;
  onSubmit?: () => void;
};

function isFileStatusEqual(fileStatus: FileStatus, otherFileStatus: FileStatus) {
  return (
    fileStatus.fieldName === otherFileStatus.fieldName && fileStatus.name === otherFileStatus.name
  );
}

function DataSectionComponent({
  loan,
  template,
  presignedUrlDetails,
  refetchDocuments,
  refetchPresignedUrlDetails,
  fileStatus,
  setFileStatus,
  documents,
  onSubmit,
}: DataSectionComponentProps) {
  const { t } = useTranslation();
  const { trackEvent } = useEventTracker();
  const { updateUserDocumentStatus } = useUserUpdateDocumentStatus(loan.data.id);
  const [uploadDialogProps, setUploadDialogProps] = useState<Field | null>();
  const toasts = useToasts();
  const { setReadyForReview, isSetReadyForReviewLoading } = useSetReadyForReview();
  const loanId = loan.data.id;

  useEffect(() => {
    trackEvent({
      event: LoansFollowupFormEvents.PublicPageView,
      data: { loan_id: loanId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetReadyForReview = (loanId: string) => {
    setReadyForReview(
      loanId ?? '',
      () => {
        toasts.positive('Email sent successfully!');
        trackEvent({
          event: LoansFollowupFormEvents.SubmitForReview,
          data: { loan_id: loan.data.id },
        });
      },
      error => {
        toasts.negative(error as Error);
        if (error instanceof Error) {
          console.error(`Failed to send email: ${error.message}`);
        } else {
          console.error('Failed to send email:', error);
        }
      },
    );
  };

  const onUpload = useCallback(
    (field: Field) => (fileInfoList: FileInfo[]) => {
      setUploadDialogProps(null);

      fileInfoList.forEach(fileInfo => {
        const fields = { ...presignedUrlDetails.fields };
        const timeStamp = new Date().getTime().toString();
        const timeStampedFileName = suffixFilename(fileInfo.file.name, timeStamp);
        fields.key = fields.key?.replace('${filename}', `${field.value}/${timeStampedFileName}`);

        const uploadedFileStatus = {
          name: timeStampedFileName,
          fieldName: field.value,
          status: 'uploading',
        } as FileStatus;

        setFileStatus(fs => [uploadedFileStatus, ...fs]);

        const onUploadSuccess = () => {
          const uploadedFileStatus = {
            name: timeStampedFileName,
            fieldName: field.value,
            status: 'uploaded',
          } as FileStatus;

          setFileStatus(fs => [
            uploadedFileStatus,
            ...fs.filter(fileStatus => !isFileStatusEqual(fileStatus, uploadedFileStatus)),
          ]);
          trackEvent({
            event: LoansFollowupFormEvents.UploadDocument,
            data: { loan_id: loan.data.id, document_type: uploadedFileStatus.fieldName },
          });
        };

        const onUploadError = (error: Error) => {
          const erroredFileStatus = {
            name: timeStampedFileName,
            fieldName: field.value,
            status: 'failed',
          } as FileStatus;

          setFileStatus(fs => [
            erroredFileStatus,
            ...fs.filter(fileStatus => !isFileStatusEqual(fileStatus, erroredFileStatus)),
          ]);

          console.error(`Failed to upload file ${timeStampedFileName}`, error);
          toasts.negative(
            new Error(`Failed to upload file ${timeStampedFileName}. Please try again.`),
          );

          refetchPresignedUrlDetails();
        };

        uploadToPresignedUrlPostRequest({
          presignedUrl: presignedUrlDetails.url,
          fields: fields,
          file: fileInfo.file,
        })
          .then(onUploadSuccess)
          .catch(onUploadError);
      });

      updateUserDocumentStatus(
        {
          documents: [{ type: field.value, status: 'REVIEWING' }],
        },
        () => refetchDocuments(),
        error => {
          toasts.negative(new Error('Failed to update document status. Please try again.'));
          console.error('Failed to update document status', error);
        },
      );
    },
    [
      presignedUrlDetails.fields,
      presignedUrlDetails.url,
      updateUserDocumentStatus,
      refetchDocuments,
      refetchPresignedUrlDetails,
      setFileStatus,
      trackEvent,
      loan.data.id,
      toasts,
    ],
  );

  const mappedData = useMemo(
    () => mapDocumentsWithSingleTemplate(documents, DocumentStatusMetadata, template),
    [documents, template],
  );

  return (
    <>
      <Box pt={8}>
        <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
          <GridAlpha.Item columnStart={2} columnEnd={27}>
            <Heading level={1} align="start" intent="secondary" variant="titleL" contrast="high">
              {t('loans.followup.user.title')}
            </Heading>
          </GridAlpha.Item>
          <GridAlpha.Item columnStart={2} columnEnd={27}>
            <Button
              id="submit"
              onClick={() => {
                handleSetReadyForReview(loanId);
                onSubmit?.();
              }}
              size="sm"
              intent="primary"
              disabled={isSetReadyForReviewLoading}
            >
              Submit for Review
            </Button>
          </GridAlpha.Item>

          <GridAlpha.Item columnStart={2} columnEnd={27}>
            <Divider />
            {mappedData.map(data => (
              <>
                <Flex direction="column" gap={4}>
                  <Text variant="bodyL">{data.label}</Text>

                  <Flex direction="row" gap={3} align="center">
                    <Button
                      onClick={() =>
                        setUploadDialogProps({
                          type: data.type,
                          value: data.value,
                          label: data.label,
                        })
                      }
                      emphasis="outlined"
                      intent="primary"
                      size="sm"
                      width="hug"
                    >
                      Upload
                    </Button>
                    <Box>
                      {/* This should be handled by the mapper for the Awaiting Upload. */}
                      <Tag intent={data.metadata?.color ?? 'warning'} size="sm" key="key">
                        {data.metadata?.label ?? 'Awaiting Upload'}
                      </Tag>
                    </Box>
                  </Flex>
                  <Box>
                    <LoansDocumentFileList
                      fileStatusList={fileStatus.filter(file => file.fieldName === data.value)}
                    />
                  </Box>
                </Flex>
                <Divider />
              </>
            ))}
          </GridAlpha.Item>

          <GridAlpha.Item columnStart={2} columnEnd={27}>
            <Button
              id="submit"
              onClick={() => {
                handleSetReadyForReview(loanId);
                onSubmit?.(); //
              }}
              size="sm"
              intent="primary"
              disabled={isSetReadyForReviewLoading}
            >
              Submit for Review
            </Button>
          </GridAlpha.Item>
        </GridAlpha>

        {!!uploadDialogProps && (
          <UploadDialog
            onClose={() => setUploadDialogProps(null)}
            open={!!uploadDialogProps}
            onUpload={onUpload(uploadDialogProps)}
          />
        )}
      </Box>
    </>
  );
}

export default LoansFollowupForm;
